import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";
import { addWsListener, connectWs, sendWs } from "../services/wsClient";

function isApiSuccess(data) {
  return data?.success === true || data?.status === "success";
}

function pickPoll(data) {
  if (!data || typeof data !== "object") return null;
  if (data.poll && typeof data.poll === "object" && (data.poll.question || data.poll.options)) {
    return data.poll;
  }
  if (data.eVoting && typeof data.eVoting === "object") return data.eVoting;
  if (data.result && typeof data.result === "object" && !Array.isArray(data.result)) {
    return data.result;
  }
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    if (data.data.poll && typeof data.data.poll === "object") return data.data.poll;
    return data.data;
  }
  if (data._id || data.id || data.question || data.eVotingId) return data;
  return null;
}

function formatDateTime(value) {
  if (value == null || value === "") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function formatCountdown(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return "Ending";
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function countdownBadgeStyle(remainingMs) {
  if (remainingMs <= 60 * 60 * 1000) {
    return { background: "rgba(239,68,68,0.12)", color: "#dc2626" };
  }
  if (remainingMs <= 24 * 60 * 60 * 1000) {
    return { background: "rgba(245,158,11,0.16)", color: "#b45309" };
  }
  return { background: "rgba(107,114,128,0.12)", color: "#374151" };
}

function displayPollStatus(status, endTime, startTime, now = Date.now()) {
  const s = String(status || "active").toLowerCase().trim();
  if (s === "ended" || s === "closed") return "ended";
  if (s === "scheduled") {
    if (startTime) {
      const start = new Date(startTime).getTime();
      if (!Number.isNaN(start) && start <= now) return "active";
    }
    return "scheduled";
  }
  if (endTime) {
    const end = new Date(endTime).getTime();
    if (!Number.isNaN(end) && end <= now) return "ended";
  }
  if (startTime) {
    const start = new Date(startTime).getTime();
    if (!Number.isNaN(start) && start > now) return "scheduled";
  }
  return "active";
}

function statusBadgeStyle(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active" || s === "open") {
    return { background: "rgba(34,197,94,0.18)", color: "#16a34a" };
  }
  if (s === "ended" || s === "closed") {
    return { background: "rgba(59,130,246,0.15)", color: "#2563eb" };
  }
  if (s === "scheduled") {
    return { background: "rgba(245,158,11,0.18)", color: "#d97706" };
  }
  return { background: "rgba(107,114,128,0.15)", color: "#4b5563" };
}

function audienceBadgeStyle(audience) {
  const a = String(audience || "").toLowerCase();
  if (a === "owner") return { background: "rgba(59,130,246,0.15)", color: "#2563eb" };
  if (a === "tenant") return { background: "rgba(245,158,11,0.18)", color: "#d97706" };
  if (a === "executive") return { background: "rgba(124,58,237,0.15)", color: "#7c3aed" };
  return { background: "rgba(14,165,233,0.15)", color: "#0284c7" };
}

function getOptionId(opt, index) {
  if (typeof opt === "string") return "";
  return String(opt?._id || opt?.id || opt?.optionId || "").trim();
}

function getOptionText(opt, index) {
  if (typeof opt === "string") return opt;
  return (
    String(opt?.text || opt?.option || opt?.label || opt?.name || "").trim() ||
    `Option ${index + 1}`
  );
}

function getOptionCount(opt, optionCounts) {
  const optionId = getOptionId(opt, 0);
  if (Array.isArray(optionCounts) && optionId) {
    const hit = optionCounts.find(
      (row) => String(row?.optionId || row?._id || row?.id || "") === optionId
    );
    if (hit && hit.count != null) return Number(hit.count);
  }
  if (opt && typeof opt === "object") {
    if (opt.votes != null) return Number(opt.votes);
    if (opt.voteCount != null) return Number(opt.voteCount);
    if (opt.count != null) return Number(opt.count);
  }
  return null;
}

function normalizeOptions(options, optionCounts) {
  if (!Array.isArray(options)) return [];
  return options.map((opt, index) => {
    const optionId = getOptionId(opt, index);
    return {
      key: optionId || `opt-${index}`,
      optionId,
      text: getOptionText(opt, index),
      votes: getOptionCount(opt, optionCounts),
      index
    };
  });
}

function extractMyOptionIds(poll, userId) {
  if (!poll) return [];
  if (Array.isArray(poll.myOptionIds) && poll.myOptionIds.length) {
    return poll.myOptionIds.map(String).filter(Boolean);
  }
  if (Array.isArray(poll.myVotes) && poll.myVotes.length) {
    return poll.myVotes
      .map((v) =>
        typeof v === "string" ? v : v?.optionId || v?._id || v?.id || ""
      )
      .map(String)
      .filter(Boolean);
  }
  const uid = String(userId || "").trim();
  if (!uid) return [];

  const list = Array.isArray(poll.votingUsers)
    ? poll.votingUsers
    : Array.isArray(poll.votes)
      ? poll.votes
      : [];

  const mine = list.find((v) => {
    const voter =
      v?.voterId?.id ||
      v?.voterId?._id ||
      (typeof v?.voterId === "string" ? v.voterId : "") ||
      v?.voterId ||
      v?.userId ||
      v?.userid ||
      v?.user;
    return String(voter || "").trim() === uid;
  });

  if (mine) {
    if (Array.isArray(mine.optionIds) && mine.optionIds.length) {
      return mine.optionIds.map(String).filter(Boolean);
    }
    if (mine.optionId) return [String(mine.optionId)];
  }

  return [];
}

function isPollVoteSocketEvent(data, eVotingId) {
  if (!data || typeof data !== "object") return false;
  const type = String(data.type || "").toLowerCase();
  const action = String(data.action || "").toLowerCase();
  const isVotePush = type === "poll_vote" || action === "vote_poll";
  if (!isVotePush) return false;

  const id = String(
    data.eVotingId ||
      data.poll?.eVotingId ||
      data.poll?.id ||
      data.poll?._id ||
      data.pollId ||
      ""
  ).trim();
  if (!eVotingId) return true;
  return !id || id === String(eVotingId);
}

function isPollEndedSocketEvent(data, eVotingId) {
  if (!data || typeof data !== "object") return false;
  const type = String(data.type || "").toLowerCase();
  const action = String(data.action || "").toLowerCase();
  const isEndPush = type === "poll_ended" || action === "end_poll";
  if (!isEndPush) return false;

  const id = String(
    data.eVotingId ||
      data.poll?.eVotingId ||
      data.poll?.id ||
      data.poll?._id ||
      data.pollId ||
      ""
  ).trim();
  if (!eVotingId) return true;
  return !id || id === String(eVotingId);
}

/**
 * Detects the explicit acknowledgement the Lambda WS handler now sends back
 * to the sender after processing a vote_poll frame. Distinct from a live
 * poll_vote push: an ack has status/pushed/skipped fields and no `poll` body.
 */
function isPollVoteAck(data) {
  if (!data || typeof data !== "object") return false;
  const action = String(data.action || "").toLowerCase();
  const type = String(data.type || "").toLowerCase();
  if (action !== "vote_poll" && type !== "poll_vote") return false;
  return (
    data.status !== undefined ||
    data.pushed !== undefined ||
    data.failed !== undefined ||
    data.skipped !== undefined
  );
}

function voterDisplayName(voter) {
  if (!voter) return "";
  if (typeof voter === "string") return voter;
  return String(voter.name || voter.userName || "").trim();
}

const DetailRow = ({ label, value }) => (
  <div className="row py-2 border-bottom" style={{ margin: 0 }}>
    <div
      className="col-12 col-sm-4 text-muted"
      style={{ fontSize: 13, fontWeight: 600 }}
    >
      {label}
    </div>
    <div className="col-12 col-sm-8" style={{ fontSize: 14 }}>
      {value != null && value !== "" ? value : "—"}
    </div>
  </div>
);

const SectionTitle = ({ children, first }) => (
  <div
    className="text-muted text-uppercase"
    style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      marginTop: first ? 0 : 18,
      marginBottom: 8
    }}
  >
    {children}
  </div>
);

function EVotingView() {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const WS_URL = process.env.REACT_APP_WEBSOCKET_URL;
  const navigate = useNavigate();
  const { id: idParam } = useParams();

  const eVotingId = useMemo(() => {
    try {
      return idParam ? decodeURIComponent(String(idParam)).trim() : "";
    } catch {
      return String(idParam || "").trim();
    }
  }, [idParam]);

  const userId = useMemo(
    () =>
      localStorage.getItem("userid") ||
      localStorage.getItem("userId") ||
      localStorage.getItem("id") ||
      "",
    []
  );

  const userRole = useMemo(
    () => String(localStorage.getItem("role") || "user").trim() || "user",
    []
  );

  const normalizedRole = useMemo(() => {
    return String(userRole || "")
      .toUpperCase()
      .replace(/[^A-Z]/g, "");
  }, [userRole]);

  const isAdmin = normalizedRole === "ADMIN" || normalizedRole === "SUPERADMIN";
  const isTenant = normalizedRole === "TENANT";

  const [loading, setLoading] = useState(true);
  const [poll, setPoll] = useState(null);
  const [selectedOptionIds, setSelectedOptionIds] = useState([]);
  const [hasVoted, setHasVoted] = useState(false);
  const [votingUsers, setVotingUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [endingPoll, setEndingPoll] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);

  useEffect(() => {
    if (!eVotingId) {
      toast.error("Invalid poll link.");
      navigate("/e-voting", { replace: true });
    }
  }, [eVotingId, navigate]);

  const applyPoll = useCallback(
    (row) => {
      if (!row || typeof row !== "object") return;
      setPoll((prev) => {
        const merged = {
          ...(prev || {}),
          ...row,
          options: Array.isArray(row.options) ? row.options : prev?.options,
          optionCounts: Array.isArray(row.optionCounts)
            ? row.optionCounts
            : row.optionCounts == null
              ? prev?.optionCounts
              : row.optionCounts,
          voteCount:
            row.voteCount != null
              ? row.voteCount
              : row.totalVotes != null
                ? row.totalVotes
                : prev?.voteCount
        };
        return merged;
      });

      if (Array.isArray(row.votingUsers)) {
        setVotingUsers(row.votingUsers);
      }

      const uid = String(userId || "").trim();
      const myIds = extractMyOptionIds(row, uid);
      const isUserInVoters =
        Boolean(uid) &&
        Array.isArray(row.votingUsers) &&
        row.votingUsers.some((v) => {
          const voter =
            v?.voterId?.id ||
            v?.voterId?._id ||
            (typeof v?.voterId === "string" ? v.voterId : "") ||
            v?.voterId ||
            v?.userId ||
            v?.userid ||
            v?.user;
          return String(voter || "").trim() === uid;
        });

      if (myIds.length) {
        setSelectedOptionIds(myIds);
        setHasVoted(true);
      } else if (
        row.alreadyVoted ||
        row.hasVoted ||
        row.voted ||
        isUserInVoters
      ) {
        setHasVoted(true);
      }
    },
    [userId]
  );

  const applyLiveVotePush = useCallback(
    (data) => {
      const pushPoll = data?.poll && typeof data.poll === "object" ? data.poll : null;
      const vote = data?.vote && typeof data.vote === "object" ? data.vote : null;

      if (pushPoll) {
        setPoll((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            eVotingId: pushPoll.eVotingId || pushPoll.id || prev.eVotingId,
            options: Array.isArray(pushPoll.options)
              ? pushPoll.options
              : prev.options,
            optionCounts: Array.isArray(pushPoll.optionCounts)
              ? pushPoll.optionCounts
              : prev.optionCounts,
            voteCount:
              pushPoll.voteCount != null
                ? pushPoll.voteCount
                : pushPoll.totalVotes != null
                  ? pushPoll.totalVotes
                  : prev.voteCount,
            votingUsers: Array.isArray(pushPoll.votingUsers)
              ? pushPoll.votingUsers
              : prev.votingUsers
          };
        });
        if (Array.isArray(pushPoll.votingUsers)) {
          setVotingUsers(pushPoll.votingUsers);
        }
      }

      if (vote) {
        const voterId =
          vote.voterId?.id ||
          vote.voterId?._id ||
          vote.voterId ||
          vote.userid ||
          vote.userId;
        if (String(voterId || "") === String(userId)) {
          const ids = Array.isArray(vote.optionIds)
            ? vote.optionIds.map(String).filter(Boolean)
            : [];
          if (ids.length) setSelectedOptionIds(ids);
          setHasVoted(true);
        }

        // Keep a local recent-voters list when backend sends vote.voterId
        if (!Array.isArray(pushPoll?.votingUsers)) {
          const name = voterDisplayName(vote.voterId);
          setVotingUsers((prev) => {
            const next = Array.isArray(prev) ? prev.slice() : [];
            const id = String(voterId || vote.id || "");
            if (id && next.some((u) => String(u?.id || u?._id || "") === id)) {
              return next;
            }
            next.unshift({
              id: voterId || vote.id,
              name: name || "",
              role: vote.voterId?.role || "",
              optionIds: vote.optionIds || [],
              votedAt: vote.votedAt
            });
            return next.slice(0, 50);
          });
        }
      }
    },
    [userId]
  );

  const fetchPoll = useCallback(
    async ({ silent = false } = {}) => {
      if (!API_BASE_URL || !eVotingId) {
        if (!API_BASE_URL && !silent) {
          toast.error("Missing REACT_APP_API_URL in environment");
        }
        setLoading(false);
        return;
      }
      if (!userId) {
        if (!silent) toast.error("Missing user id. Please login again.");
        setLoading(false);
        return;
      }

      let didIncrement = false;
      try {
        if (!silent) {
          loaderIncrement();
          didIncrement = true;
        }
        const response = await axios.post(API_BASE_URL, {
          action: "eVoting.getById",
          userid: userId,
          eVotingId
        });
        const data = response?.data;
        if (!isApiSuccess(data)) {
          if (!silent) {
            toast.error(data?.message || data?.error || "Failed to load poll");
          }
          setPoll(null);
          return;
        }
        const row = pickPoll(data);
        if (!row) {
          if (!silent) toast.error(data?.message || "Poll not found in response");
          setPoll(null);
          return;
        }
        applyPoll(row);
      } catch (error) {
        if (!silent) {
          toast.error(
            error?.response?.data?.message || error?.message || "Failed to load poll"
          );
        }
        setPoll(null);
      } finally {
        if (didIncrement) loaderDecrement();
        setLoading(false);
      }
    },
    [API_BASE_URL, eVotingId, userId, applyPoll]
  );

  // Called when the configured end time is reached. Persists the end through
  // the backend API (instead of only waiting for the next fetch), so the poll
  // actually ends on schedule for every viewer.
  const autoEndPoll = useCallback(async () => {
    if (!API_BASE_URL || !eVotingId || !userId) return;
    try {
      await axios.post(API_BASE_URL, {
        action: "eVoting.autoEnd",
        userid: userId,
        eVotingId
      });
    } catch (error) {
      // Ignore transient errors; fetchPoll below shows the authoritative state.
    } finally {
      fetchPoll({ silent: true });
    }
  }, [API_BASE_URL, eVotingId, userId, fetchPoll]);

  useEffect(() => {
    fetchPoll();
  }, [fetchPoll]);

  const applyPollEnded = useCallback(
    (data) => {
      const pushPoll = data?.poll && typeof data.poll === "object" ? data.poll : null;
      setPoll((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          status: "ended",
          options: Array.isArray(pushPoll?.options) ? pushPoll.options : prev.options,
          optionCounts: Array.isArray(pushPoll?.optionCounts)
            ? pushPoll.optionCounts
            : prev.optionCounts,
          voteCount:
            pushPoll?.voteCount != null ? pushPoll.voteCount : prev.voteCount,
          votingUsers: Array.isArray(pushPoll?.votingUsers)
            ? pushPoll.votingUsers
            : prev.votingUsers
        };
      });
      if (Array.isArray(pushPoll?.votingUsers)) {
        setVotingUsers(pushPoll.votingUsers);
      }
      // Re-fetch so results/voter visibility match this viewer's role
      fetchPoll({ silent: true });
    },
    [fetchPoll]
  );

  // Ensure shared socket is up ($connect with userId) so Lambda fan-out can reach this client
  useEffect(() => {
    if (!eVotingId || !userId || !WS_URL) return undefined;

    connectWs({ baseUrl: WS_URL, userId, userType: userRole }).catch(() => { });

    const unsubscribe = addWsListener((data) => {
      if (isPollEndedSocketEvent(data, eVotingId)) {
        applyPollEnded(data);
        return;
      }
      // Scheduled poll went live
      const type = String(data?.type || "").toLowerCase();
      const action = String(data?.action || "").toLowerCase();
      const isStartPush = type === "poll_started" || action === "poll_started";
      if (isStartPush) {
        const pushId = String(
          data.eVotingId || data.poll?.eVotingId || data.poll?.id || data.poll?._id || data.pollId || ""
        ).trim();
        if (!pushId || pushId === String(eVotingId)) {
          setPoll((prev) => prev ? { ...prev, status: "active" } : prev);
          setNow(Date.now());
        }
        return;
      }
      if (isPollVoteAck(data)) {
        // Explicit acknowledgement from the Lambda WS handler: fan-out status
        // for the vote the local user just cast. Other clients receive the
        // live poll_vote push separately via applyLiveVotePush.
        console.log("[ws] vote_poll acknowledgement:", {
          eVotingId: data.eVotingId,
          status: data.status,
          pushed: data.pushed,
          failed: data.failed,
          skipped: data.skipped,
          reason: data.reason,
          recipientCount: data.recipientCount
        });
        return;
      }
      if (!isPollVoteSocketEvent(data, eVotingId)) return;
      applyLiveVotePush(data);
    });

    return unsubscribe;
  }, [eVotingId, userId, userRole, WS_URL, applyLiveVotePush, applyPollEnded]);

  const question = poll?.question || "—";
  const audience = String(poll?.audience || "All").trim() || "All";
  const allowMultiple = Boolean(poll?.allowMultipleAnswers);
  const hasEndTime = Boolean(poll?.setEndTime || poll?.endTime);
  const options = normalizeOptions(poll?.options, poll?.optionCounts);
  const [now, setNow] = useState(() => Date.now());
  const [countdownNow, setCountdownNow] = useState(() => Date.now());
  const status = displayPollStatus(poll?.status, poll?.endTime, poll?.startTime, now);
  const isActive = status === "active";
  const isScheduled = status === "scheduled";
  const endMs = poll?.endTime ? new Date(poll.endTime).getTime() : NaN;
  const startMs = poll?.startTime ? new Date(poll.startTime).getTime() : NaN;
  const remainingMs = Number.isFinite(endMs) ? endMs - countdownNow : NaN;
  const startsInMs = Number.isFinite(startMs) ? startMs - countdownNow : NaN;
  const showEndCountdown = isActive && Number.isFinite(endMs);
  const showStartCountdown = isScheduled && Number.isFinite(startMs);

  // Keep the header countdown live without touching `now`: `now` drives the
  // auto-end timer effect, and changing it early would clear that timer.
  useEffect(() => {
    if (!isActive && !isScheduled) return undefined;
    const targetMs = isScheduled ? startMs : endMs;
    if (!Number.isFinite(targetMs)) return undefined;
    let timer = null;
    const tick = () => {
      const current = Date.now();
      setCountdownNow(current);
      const remaining = targetMs - current;
      if (remaining <= 0) return;
      timer = setTimeout(tick, remaining > 24 * 60 * 60 * 1000 ? 60000 : 1000);
    };
    tick();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isActive, isScheduled, startMs, endMs]);

  useEffect(() => {
    if (!poll?.endTime) return undefined;
    if (!isActive) return undefined;
    const endMs = new Date(poll.endTime).getTime();
    if (Number.isNaN(endMs)) return undefined;
    const diff = endMs - Date.now();

    if (diff <= 0) {
      // Page opened after the scheduled time: persist the end now.
      setNow(Date.now());
      autoEndPoll();
      return undefined;
    }

    // Browsers clamp setTimeout delays above ~24.8 days (2^31-1 ms).
    // Long polls (e.g. 3 months) must re-arm in chunks instead of firing early.
    const MAX_TIMER_DELAY = 24 * 24 * 60 * 60 * 1000; // 24 days
    let timer = null;
    const schedule = () => {
      const remaining = endMs - Date.now();
      if (remaining <= 0) {
        setNow(Date.now());
        autoEndPoll();
        return;
      }
      timer = setTimeout(() => {
        if (Date.now() >= endMs) {
          setNow(Date.now());
          autoEndPoll();
        } else {
          schedule();
        }
      }, Math.min(remaining + 500, MAX_TIMER_DELAY));
    };

    schedule();

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [poll?.endTime, isActive, autoEndPoll]);

  // When a scheduled poll's startTime arrives, flip status locally
  // (the WS poll_started event also does this, but this timer covers
  // the case where the WS message is missed).
  useEffect(() => {
    if (!poll?.startTime) return undefined;
    if (!isScheduled) return undefined;
    const sMs = new Date(poll.startTime).getTime();
    if (Number.isNaN(sMs)) return undefined;
    const diff = sMs - Date.now();
    if (diff <= 0) {
      setNow(Date.now());
      return undefined;
    }
    const MAX_TIMER_DELAY = 24 * 24 * 60 * 60 * 1000;
    let timer = null;
    const schedule = () => {
      const remaining = sMs - Date.now();
      if (remaining <= 0) { setNow(Date.now()); return; }
      timer = setTimeout(() => {
        if (Date.now() >= sMs) setNow(Date.now());
        else schedule();
      }, Math.min(remaining + 500, MAX_TIMER_DELAY));
    };
    schedule();
    return () => { if (timer) clearTimeout(timer); };
  }, [poll?.startTime, isScheduled]);

  const isPollEnded = !isActive && !isScheduled;

  const canVote = !isPollEnded && isActive && !isScheduled && !hasVoted;

  const createdById = useMemo(() => {
    return String(
      poll?.createdById ||
      poll?.createdBy?._id ||
      poll?.createdBy?.id ||
      (typeof poll?.createdBy === "string" ? poll?.createdBy : "") ||
      ""
    ).trim();
  }, [poll]);

  const pollAdminId = useMemo(() => {
    return String(
      poll?.adminId?._id ||
      poll?.adminId?.id ||
      (typeof poll?.adminId === "string" ? poll?.adminId : "") ||
      ""
    ).trim();
  }, [poll]);

  const isCreator = useMemo(() => {
    return Boolean(createdById && userId && createdById === userId);
  }, [createdById, userId]);

  const isPollAdmin = useMemo(() => {
    return Boolean(pollAdminId && userId && pollAdminId === userId);
  }, [pollAdminId, userId]);

  // Admin / creator always see the tallies. The audience sees them only once
  // the poll has ended (manually or automatically by endTime). While the poll
  // is live, the audience sees just the options and their own choice.
  const showResults = Boolean(isAdmin || isPollAdmin || isCreator || isPollEnded);

  const totalVotes =
    poll?.voteCount != null
      ? Number(poll.voteCount)
      : poll?.totalVotes != null
        ? Number(poll.totalVotes)
        : options.some((o) => o.votes != null)
          ? options.reduce((sum, o) => sum + (Number(o.votes) || 0), 0)
          : null;

  // The option text(s) this viewer personally selected — shown to the audience
  // instead of the tallies.
  const myVotedOptionTexts = useMemo(() => {
    if (!hasVoted) return [];
    return options
      .filter((o) => selectedOptionIds.includes(o.optionId))
      .map((o) => o.text)
      .filter(Boolean);
  }, [hasVoted, options, selectedOptionIds]);

  // Audience sees the full option list; the option(s) they picked are marked
  // as "Your choice". Admin / creator additionally see the tallies.

  // Only the admin or the specific creator (matched by _id) can view voters
  const canViewVoters = useMemo(() => {
    if (isTenant) return false;
    if (poll?.canViewVoters !== undefined) {
      return Boolean(poll.canViewVoters);
    }
    return Boolean(isAdmin || isPollAdmin || isCreator);
  }, [isTenant, poll, isAdmin, isPollAdmin, isCreator]);

  const visibleVoters = useMemo(() => {
    if (!canViewVoters) return [];
    if (Array.isArray(votingUsers) && votingUsers.length) return votingUsers;
    if (Array.isArray(poll?.votingUsers)) return poll.votingUsers;
    return [];
  }, [canViewVoters, votingUsers, poll]);

  // Only the poll creator (or the apartment admin) can end a live poll.
  // Guard on `poll` so the button never flashes before data loads.
  const canEndPoll = useMemo(() => {
    if (!poll || !isActive) return false;
    if (hasEndTime) return false; // poll has a scheduled end time — no manual end needed
    return Boolean(isCreator || isPollAdmin || isAdmin);
  }, [poll, isActive, hasEndTime, isCreator, isPollAdmin, isAdmin]);

  // Only scheduled polls can be edited
  const canEdit = isScheduled && Boolean(isCreator || isPollAdmin || isAdmin);

  const handleEndPoll = async () => {
    if (!canEndPoll || endingPoll) return;
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }
    if (!userId) {
      toast.error("Missing user id. Please login again.");
      return;
    }

    setShowEndConfirm(false);
    setEndingPoll(true);
    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(API_BASE_URL, {
        action: "eVoting.end",
        userid: userId,
        eVotingId
      });
      const data = response?.data;
      if (!isApiSuccess(data)) {
        toast.error(data?.message || data?.error || "Failed to end poll");
        return;
      }

      toast.success(data?.message || "Poll ended");
      const updated = pickPoll(data);
      if (updated) applyPoll({ ...updated, status: "ended" });
      setPoll((prev) => (prev ? { ...prev, status: "ended" } : prev));

      // Live status flip over the socket so everyone with the poll open sees it end.
      const broadcastPoll = updated || data?.poll || null;
      sendWs({
        action: "poll_ended",
        type: "poll_ended",
        userId,
        userType: userRole,
        eVotingId,
        audience: data?.audience ?? null,
        poll: broadcastPoll,
        recipientIds: Array.isArray(data?.recipientIds) ? data.recipientIds : null
      });

      await fetchPoll({ silent: true });
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || "Failed to end poll"
      );
    } finally {
      if (didIncrement) loaderDecrement();
      setEndingPoll(false);
    }
  };

  const toggleOption = (optionId) => {
    if (hasVoted || !canVote || submitting || !optionId) return;
    setSelectedOptionIds((prev) => {
      if (allowMultiple) {
        return prev.includes(optionId)
          ? prev.filter((id) => id !== optionId)
          : [...prev, optionId];
      }
      return prev.includes(optionId) ? [] : [optionId];
    });
  };

  const handleVote = async (event) => {
    event.preventDefault();
    if (hasVoted) {
      toast.info("You have already voted on this poll.");
      return;
    }
    if (!canVote) return;
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }
    if (!userId) {
      toast.error("Missing user id. Please login again.");
      return;
    }

    let optionIds = selectedOptionIds.map(String).filter(Boolean);
    if (!allowMultiple && optionIds.length > 1) {
      optionIds = optionIds.slice(0, 1);
    }
    if (!optionIds.length) {
      toast.error(allowMultiple ? "Select at least one option" : "Select an option");
      return;
    }
    if (options.some((o) => !o.optionId)) {
      toast.error("Poll options are missing ids. Reload and try again.");
      return;
    }

    // HTTP save — Lambda fans out poll_vote to audience (+ admin)
    const apiPayload = {
      action: "eVoting.vote",
      userid: userId,
      eVotingId,
      optionIds
    };

    setSubmitting(true);
    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(API_BASE_URL, apiPayload);
      const data = response?.data;
      if (!isApiSuccess(data)) {
        toast.error(data?.message || data?.error || "Failed to submit vote");
        return;
      }

      toast.success(data?.message || "Vote submitted");
      setHasVoted(true);
      setSelectedOptionIds(optionIds);

      const updated = pickPoll(data);
      if (updated) {
        applyPoll(updated);
      } else if (data?.poll) {
        applyLiveVotePush(data);
      } else {
        await fetchPoll({ silent: true });
      }

      // Live fan-out through the socket so every open poll (creator/admin/audience)
      // reflects the new tallies without a refresh. Send the snapshots (not optionIds)
      // so the backend fan-out does NOT re-cast the already-persisted ballot.
      const broadcastPoll = updated || data?.poll || null;

      // Ensure the socket is open before we fan out, so the sender's own screen
      // and other open tabs stay in sync. (The server also fans out independently.)
      const WS_URL = process.env.REACT_APP_WEBSOCKET_URL;
      if (WS_URL && userId) {
        try {
          await connectWs({ baseUrl: WS_URL, userId, userType: userRole });
        } catch {
          // socket may already be open; ignore and still attempt sendWs below
        }
      }

      sendWs({
        action: "vote_poll",
        type: "poll_vote",
        userId,
        userType: userRole,
        eVotingId,
        audience: data?.audience ?? null,
        vote: data?.vote ?? null,
        poll: broadcastPoll,
        recipientIds: Array.isArray(data?.recipientIds) ? data.recipientIds : null
      });
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || "Failed to submit vote"
      );
    } finally {
      if (didIncrement) loaderDecrement();
      setSubmitting(false);
    }
  };

  return (
    <div className="employee-form">
      <div className="content-header">
        <div
          className="container-fluid d-flex justify-content-between align-items-end flex-wrap"
          style={{ gap: 12 }}
        >
          <div>
            <nav className="text-muted mb-1" style={{ fontSize: 13 }}>
              <Link to="/e-voting">E-Voting</Link>
              <span className="mx-2">/</span>
              <span>View</span>
            </nav>
            <h1 className="m-0 txt_lft">Poll details</h1>

          </div>
          <div className="d-flex flex-wrap" style={{ gap: 8 }}>
            {canEdit ? (
              <button
                type="button"
                className="btn btn-warning"
                onClick={() => navigate(`/e-voting-edit/${eVotingId}`)}
              >
                <i className="fa-solid fa-pen-to-square mr-1" aria-hidden />
                Edit poll
              </button>
            ) : null}
            {canEndPoll ? (
              <button
                type="button"
                className="btn btn-blue"
                onClick={() => setShowEndConfirm(true)}
                disabled={endingPoll || submitting}
              >
                <i className="fa-solid fa-stop-circle mr-1" aria-hidden />
                {endingPoll ? "Ending…" : "End poll"}
              </button>
            ) : null}
            <Link to="/e-voting" className="btn btn-secondary">
              <i className="fa-solid fa-arrow-left mr-1" /> Back
            </Link>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          {loading && !poll ? (
            <div className="card">
              <div className="card-body text-muted">Loading poll…</div>
            </div>
          ) : poll ? (
            <div className="row">
              <div className="col-12 col-lg-8 mb-3">
                <div className="card">
                  <div
                    className="card-header d-flex align-items-center flex-wrap"
                    style={{ gap: 8 }}
                  >
                    {/* Left: title */}
                    <h3 className="card-title mb-0">Question</h3>

                    {/* Centre: audience + status badges */}
                    <div className="d-flex flex-wrap justify-content-center" style={{ gap: 8, flex: 1 }}>
                      <span
                        className="badge"
                        style={{
                          padding: "0.35rem 0.65rem",
                          borderRadius: 999,
                          fontWeight: 600,
                          fontSize: 11,
                          ...audienceBadgeStyle(audience)
                        }}
                      >
                        {audience.toUpperCase()}
                      </span>
                      <span
                        className="badge"
                        style={{
                          padding: "0.35rem 0.65rem",
                          borderRadius: 999,
                          fontWeight: 600,
                          fontSize: 11,
                          ...statusBadgeStyle(status)
                        }}
                      >
                        {status.toUpperCase()}
                      </span>
                    </div>

                    {/* Right: countdown badge */}
                    <div className="d-flex flex-wrap" style={{ gap: 8, marginLeft: "auto" }}>
                      {showStartCountdown ? (
                        <span
                          className="badge"
                          title={`Starts ${formatDateTime(poll.startTime)}`}
                          style={{
                            padding: "0.35rem 0.65rem",
                            borderRadius: 999,
                            display: "inline-flex",
                            alignItems: "center",
                            whiteSpace: "nowrap",
                            background: "rgba(245,158,11,0.18)",
                            color: "#d97706"
                          }}
                        >
                          <i className="fas fa-hourglass-half" aria-hidden="true" />
                          <span style={{ fontSize: 14, fontWeight: 600, marginLeft: 8 }}>
                            {startsInMs > 0
                              ? `Starts in ${formatCountdown(startsInMs)}`
                              : "Starting…"}
                          </span>
                        </span>
                      ) : null}
                      {showEndCountdown ? (
                        <span
                          className="badge"
                          title={`Ends ${formatDateTime(poll.endTime)}`}
                          style={{
                            padding: "0.35rem 0.65rem",
                            borderRadius: 999,
                            display: "inline-flex",
                            alignItems: "center",
                            whiteSpace: "nowrap",
                            ...countdownBadgeStyle(remainingMs)
                          }}
                        >
                          <i className="fas fa-clock" aria-hidden="true" />
                          <span style={{ fontSize: 14, fontWeight: 600, marginLeft: 8 }}>
                            {remainingMs > 0
                              ? `Ends in ${formatCountdown(remainingMs)}`
                              : "Ending…"}
                          </span>
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="card-body">
                    <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 0 }}>
                      {question}
                    </p>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title mb-0">
                      {hasVoted
                        ? showResults
                          ? "Results"
                          : "Your vote"
                        : canVote
                          ? "Cast your vote"
                          : showResults
                            ? "Results"
                            : "Poll closed"}
                    </h3>
                  </div>
                  <div className="card-body p-0">
                    {options.length ? (
                      <form onSubmit={handleVote}>
                        <ul className="list-group list-group-flush">
                          {options.map((opt, index) => {
                            const pct =
                              totalVotes > 0 && opt.votes != null
                                ? Math.round((Number(opt.votes) / totalVotes) * 100)
                                : null;
                            const checked = selectedOptionIds.includes(opt.optionId);
                            const inputType = allowMultiple ? "checkbox" : "radio";
                            const showCounts = showResults;

                            return (
                              <li
                                key={opt.key}
                                className="list-group-item"
                                style={{
                                  cursor: canVote && !hasVoted && opt.optionId ? "pointer" : "default",
                                  background: checked ? "rgba(0,148,169,0.06)" : undefined
                                }}
                                onClick={() => toggleOption(opt.optionId)}
                              >
                                <div
                                  className="d-flex justify-content-between align-items-center flex-wrap"
                                  style={{ gap: 8 }}
                                >
                                  <label
                                    htmlFor={`evoting-opt-${opt.key}`}
                                    className="d-flex align-items-center mb-0"
                                    style={{
                                      gap: 10,
                                      cursor: canVote && !hasVoted && opt.optionId ? "pointer" : "default"
                                    }}
                                  >
                                    {isActive ? (
                                      <input
                                        id={`evoting-opt-${opt.key}`}
                                        type={inputType}
                                        name="evoting-option"
                                        checked={checked}
                                        onChange={() => toggleOption(opt.optionId)}
                                        onClick={(e) => e.stopPropagation()}
                                        disabled={hasVoted || isPollEnded || submitting || !opt.optionId}
                                        style={{ cursor: hasVoted || isPollEnded ? "not-allowed" : "pointer" }}
                                      />
                                    ) : (
                                      <span
                                        className="text-muted"
                                        style={{ fontSize: 12, minWidth: 18 }}
                                      >
                                        {index + 1}.
                                      </span>
                                    )}
                                    <span style={{ fontWeight: 500 }}>{opt.text}</span>
                                  </label>
                                  {showCounts && opt.votes != null ? (
                                    <span className="text-muted" style={{ fontSize: 13 }}>
                                      {opt.votes} vote{Number(opt.votes) === 1 ? "" : "s"}
                                      {pct != null ? ` (${pct}%)` : ""}
                                    </span>
                                  ) : null}
                                  {!showResults && hasVoted && checked ? (
                                    <span
                                      className="badge"
                                      style={{
                                        background: "#0094a9",
                                        color: "#fff",
                                        fontSize: 11,
                                        padding: "0.3rem 0.55rem",
                                        borderRadius: 999
                                      }}
                                    >
                                      Your choice
                                    </span>
                                  ) : null}
                                </div>
                                {showCounts && pct != null ? (
                                  <div
                                    className="progress mt-2"
                                    style={{ height: 6, background: "#e9ecef" }}
                                  >
                                    <div
                                      className="progress-bar"
                                      role="progressbar"
                                      style={{
                                        width: `${pct}%`,
                                        background: "#0094a9"
                                      }}
                                      aria-valuenow={pct}
                                      aria-valuemin={0}
                                      aria-valuemax={100}
                                    />
                                  </div>
                                ) : null}
                              </li>
                            );
                          })}
                        </ul>
                        <div
                          className="p-3 border-top d-flex align-items-center flex-wrap"
                          style={{ gap: 12 }}
                        >
                          {!(hasVoted && !showResults) ? (
                            <button
                              type="submit"
                              className="btn btn-primary"
                              disabled={hasVoted || submitting || !selectedOptionIds.length || isPollEnded || !isActive}
                              style={
                                hasVoted || isPollEnded || !isActive
                                  ? { opacity: 0.65, cursor: "not-allowed" }
                                  : undefined
                              }
                            >
                              {submitting ? "Submitting…" : "Submit vote"}
                            </button>
                          ) : null}
                          {hasVoted ? (
                            <span
                              className="text-muted d-inline-flex align-items-center"
                              style={{ fontSize: 13, gap: 6 }}
                            >
                              <i className="fa-solid fa-circle-check text-success" />
                              {showResults
                                ? "You have already voted on this poll."
                                : myVotedOptionTexts.length
                                  ? `Your vote: ${myVotedOptionTexts.join(", ")}`
                                  : "You have already voted on this poll."}
                            </span>
                          ) : isScheduled ? (
                            <span className="text-muted" style={{ fontSize: 13 }}>
                              <i className="fas fa-hourglass-half mr-1" aria-hidden />
                              This poll hasn't started yet. Voting opens at{" "}
                              {formatDateTime(poll?.startTime)}.
                            </span>
                          ) : isPollEnded || !isActive ? (
                            <span className="text-muted" style={{ fontSize: 13 }}>
                              This poll has ended and is not accepting votes.
                            </span>
                          ) : allowMultiple ? (
                            <small className="text-muted">
                              Multiple answers allowed
                            </small>
                          ) : (
                            <small className="text-muted">
                              Select one option
                            </small>
                          )}
                        </div>
                      </form>
                    ) : (
                      <div className="p-3 text-muted">No options</div>
                    )}
                  </div>
                </div>

                {canViewVoters && isPollEnded && visibleVoters.length ? (
                  <div className="card">
                    <div className="card-header">
                      <h3 className="card-title mb-0">Voters</h3>
                    </div>
                    <div className="card-body p-0">
                      <ul className="list-group list-group-flush">
                        {visibleVoters.map((voter, idx) => {
                          const name =
                            voterDisplayName(voter) ||
                            voterDisplayName(voter?.voterId) ||
                            "Voter";
                          const role = String(
                            voter?.role || voter?.voterId?.role || ""
                          ).trim();
                          const when = formatDateTime(voter?.votedAt);
                          return (
                            <li
                              key={String(voter?.id || voter?._id || `${name}-${idx}`)}
                              className="list-group-item d-flex justify-content-between align-items-center flex-wrap"
                              style={{ gap: 8 }}
                            >
                              <span style={{ fontWeight: 500 }}>
                                {name}
                                {role ? (
                                  <span className="text-muted" style={{ fontSize: 12 }}>
                                    {" "}
                                    ({role})
                                  </span>
                                ) : null}
                              </span>
                              <span className="text-muted" style={{ fontSize: 12 }}>
                                {when}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="col-12 col-lg-4 mb-3">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title mb-0">Details</h3>
                  </div>
                  <div className="card-body">
                    <SectionTitle first>Settings</SectionTitle>
                    <DetailRow
                      label="Multiple answers"
                      value={allowMultiple ? "Yes" : "No"}
                    />
                    {showResults ? (
                      <DetailRow
                        label="Total votes"
                        value={totalVotes != null ? String(totalVotes) : "—"}
                      />
                    ) : null}

                    <SectionTitle>Schedule</SectionTitle>
                    <DetailRow
                      label="Created"
                      value={formatDateTime(poll?.createdAt)}
                    />
                    {poll?.setStartTime || poll?.startTime ? (
                      <DetailRow
                        label="Start time"
                        value={formatDateTime(poll?.startTime)}
                      />
                    ) : null}
                    <DetailRow
                      label="End time"
                      value={hasEndTime ? formatDateTime(poll?.endTime) : "Not set"}
                    />
                    {poll?.updatedAt ? (
                      <DetailRow
                        label="Updated"
                        value={formatDateTime(poll.updatedAt)}
                      />
                    ) : null}

                    {(poll?.createdByName ||
                      poll?.createdBy ||
                      poll?.createdById) && (
                        <>
                          <SectionTitle>Created by</SectionTitle>
                          <DetailRow
                            label="User"
                            value={
                              poll?.createdByName ||
                              poll?.createdBy?.name ||
                              String(poll?.createdBy || poll?.createdById || "")
                            }
                          />
                        </>
                      )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body text-muted">
                Poll not found.{" "}
                <Link to="/e-voting">Back to E-Voting</Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {showEndConfirm && canEndPoll ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16
          }}
          onClick={() => {
            if (!endingPoll) setShowEndConfirm(false);
          }}
        >
          <div
            className="card app-modal-card"
            style={{ width: "min(440px, 96vw)", maxHeight: "90vh", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header app-modal-header">
              <div className="app-modal-title">End poll</div>
              <button
                type="button"
                className="app-modal-close"
                onClick={() => setShowEndConfirm(false)}
                disabled={endingPoll}
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div className="card-body">
              Are you sure you want to end this poll? Members will no longer be
              able to vote, and the results will be shown to everyone.
            </div>

            <div
              className="card-footer d-flex justify-content-end"
              style={{ gap: 10 }}
            >
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowEndConfirm(false)}
                disabled={endingPoll}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-blue"
                onClick={handleEndPoll}
                disabled={endingPoll}
              >
                {endingPoll ? "Ending…" : "End poll"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default EVotingView;
